import { useSearchModifiers } from 'controllers/SearchController/hooks/useSearchModifiers';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';
import { useCallback, useState, useEffect } from 'react';
import { FiltersParams } from 'types/params';
import { getActiveSortingType } from './DropdownSorting';

export enum SamplesTableSortingTypes {
    Name = 'name',
    Time = 'time',
    Type = 'type',
    Bpm = 'bpm',
    Key = 'key',
}

export enum DropdownSortingTypes {
    Trending = 'trending',
    Recent = 'recent',
    NameAsc = 'nameAsc',
    NameDesc = 'nameDesc',
}

export enum SamplesTableSortingDirection {
    Ascending = 'ascending',
    Descending = 'descending',
}

interface UseSortingOptionProps {
    type: DropdownSortingTypes | SamplesTableSortingTypes | null;
}

const useSortingOption = ({ type }: UseSortingOptionProps) => {
    const { addFilter, clearByFilter } = useSearchModifiers();
    const [tableFilterClickCount, setTableFilterClickCount] = useState(0);

    const { orderby, orderbydirection } = useCurrentSearchState();
    const activeSortingType = getActiveSortingType({
        orderby,
        orderbydirection,
    });

    const isActive =
        activeSortingType === type ||
        (activeSortingType === undefined &&
            type === DropdownSortingTypes.Trending);
    const isTableSorting = Object.values(SamplesTableSortingTypes).includes(
        type as SamplesTableSortingTypes,
    );

    const resetFilters = useCallback(() => {
        clearByFilter(FiltersParams.orderby);
        clearByFilter(FiltersParams.orderbydirection);
        setTableFilterClickCount(0);
    }, [clearByFilter]);

    useEffect(() => {
        if (tableFilterClickCount === 2) {
            resetFilters();
        }
    }, [tableFilterClickCount, resetFilters]);

    const callback = useCallback(() => {
        if (!type) {
            return;
        }

        if (isTableSorting) {
            let direction = SamplesTableSortingDirection.Ascending;

            if (orderby === type) {
                setTableFilterClickCount((prev) => prev + 1);
                direction =
                    orderbydirection === SamplesTableSortingDirection.Descending
                        ? SamplesTableSortingDirection.Ascending
                        : SamplesTableSortingDirection.Descending;
            } else {
                setTableFilterClickCount(0);
            }

            addFilter([
                [FiltersParams.orderby, type],
                [FiltersParams.orderbydirection, direction],
            ]);
        } else {
            if (type === DropdownSortingTypes.NameAsc) {
                addFilter([
                    [FiltersParams.orderby, 'name'],
                    [FiltersParams.orderbydirection, 'ascending'],
                ]);
            } else if (type === DropdownSortingTypes.NameDesc) {
                addFilter([
                    [FiltersParams.orderby, 'name'],
                    [FiltersParams.orderbydirection, 'descending'],
                ]);
            } else if (type) {
                addFilter([[FiltersParams.orderby, type.toLowerCase()]]);
                clearByFilter(FiltersParams.orderbydirection);
            }
        }
    }, [
        addFilter,
        clearByFilter,
        isTableSorting,
        orderby,
        orderbydirection,
        type,
        setTableFilterClickCount,
    ]);

    return {
        callback,
        isActive,
        currentType: orderby,
        currentDirection: orderbydirection,
        resetFilters,
    };
};

export default useSortingOption;
